import { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useInView } from 'react-intersection-observer';

const springTransition = {
  type: 'spring',
  stiffness: 80,
  damping: 20,
};

interface Phase {
  label: string;
  timeline: string;
  icon: string;
  clientActions: string[];
  thoughticActions: string[];
  deliverables: string[];
  highlight: string;
}

const phases: Phase[] = [
  {
    label: 'Kickoff Call',
    timeline: 'Day 1',
    icon: 'ri-video-chat-line',
    clientActions: [
      'Meet your dedicated account manager',
      'Share codebase access and documentation',
      'Define communication channels and standup times',
      'Introduce your in-house team to new engineers',
    ],
    thoughticActions: [
      'Assign a dedicated account manager to your engagement',
      'Set up secure VPN and isolated development environment',
      'Provision company hardware or configure MDM on BYOD',
      'Walk through security protocols and NDA compliance',
    ],
    deliverables: ['Account manager intro', 'Security checklist signed', 'VPN credentials issued'],
    highlight: 'Your engineer is briefed and ready within 4 hours of contract signing.',
  },
  {
    label: 'Environment Setup',
    timeline: 'Day 1–3',
    icon: 'ri-server-line',
    clientActions: [
      'Grant repository and staging environment access',
      'Share architecture docs and coding standards',
      'Schedule first-week pair-programming sessions',
    ],
    thoughticActions: [
      'Clone repos, run local builds, and resolve dependency issues',
      'Audit codebase health and flag any blockers',
      'Set up IDE tooling, linters, and CI/CD hooks per your standards',
      'Conduct internal peer review of first commits',
    ],
    deliverables: ['Local dev environment green', 'First commit merged', 'CI/CD pipeline passing'],
    highlight: '97% of our engineers push meaningful code within the first 72 hours.',
  },
  {
    label: 'First Sprint',
    timeline: 'Week 1',
    icon: 'ri-run-line',
    clientActions: [
      'Assign a scoped starter ticket from your backlog',
      'Attend daily standups to provide context',
      'Give real-time feedback on PRs and approach',
    ],
    thoughticActions: [
      'Engineer delivers first story end-to-end with tests',
      'Account manager checks in daily for the first 5 days',
      'Escalate any integration or access friction same-day',
      'Capture feedback to tune the match if needed',
    ],
    deliverables: ['First user story shipped', 'Daily standup rhythm established', 'Feedback captured'],
    highlight: 'Your engineer ships a production ticket by Day 5 on average.',
  },
  {
    label: 'Performance Review',
    timeline: 'Day 14',
    icon: 'ri-bar-chart-grouped-line',
    clientActions: [
      'Rate velocity, code quality, and communication in a 10-min survey',
      'Flag any gaps or request skill adjustments',
      'Decide whether to scale the team or refine the role',
    ],
    thoughticActions: [
      'Deliver a structured 14-day performance snapshot',
      'Propose a growth plan or swap if satisfaction is below 4/5',
      'Begin sourcing additional engineers if you want to scale',
      'Document lessons learned for continuous improvement',
    ],
    deliverables: ['14-day scorecard', 'Growth plan or swap initiated', 'Scale plan drafted'],
    highlight: 'Not happy? We begin a replacement search immediately — no questions asked.',
  },
  {
    label: 'Full Integration',
    timeline: 'Day 30',
    icon: 'ri-stack-line',
    clientActions: [
      'Treat the engineer as a core team member in retros',
      'Assign increasingly complex architecture work',
      'Provide quarterly roadmap context for long-term alignment',
    ],
    thoughticActions: [
      'Engineer attends sprint planning, retros, and demos as a full member',
      'Account manager shifts to bi-weekly strategic check-ins',
      'Quarterly business reviews to align staffing with roadmap',
      'Monitor retention signals and proactively address burnout risks',
    ],
    deliverables: ['Monthly velocity report', 'Quarterly roadmap alignment', 'Retention health check'],
    highlight: 'By Day 30, 94% of clients say the engineer feels like a full teammate.',
  },
  {
    label: 'Scale & Optimize',
    timeline: 'Day 60–90',
    icon: 'ri-rocket-line',
    clientActions: [
      'Define next hiring targets based on roadmap velocity',
      'Request specialized skills (DevOps, ML, mobile) as needs evolve',
      'Co-design team structure for long-term growth',
    ],
    thoughticActions: [
      'Source and onboard additional engineers with 2-week turnaround',
      'Rotate in specialists for short-term spikes without long contracts',
      'Conduct 90-day comprehensive engagement review',
      'Renew or restructure the agreement based on outcomes',
    ],
    deliverables: ['90-day outcomes report', 'Team scaling executed', 'Contract renewal or pivot'],
    highlight: 'Average client scales from 1 to 4 engineers within the first 90 days.',
  },
];

const comparison = [
  {
    typical: '1–2 week lead time before first contact',
    thoughtic: 'Account manager calls within 2 hours of signing',
    metric: '10x faster',
  },
  {
    typical: 'No structured onboarding checklist',
    thoughtic: '72-item security + environment checklist completed in 3 days',
    metric: '72 checkpoints',
  },
  {
    typical: 'First code contribution in week 2–3',
    thoughtic: 'Production code shipped by Day 5 on average',
    metric: '71% sooner',
  },
  {
    typical: 'Feedback loop after 30–60 days',
    thoughtic: '14-day performance review with swap guarantee',
    metric: '4x earlier',
  },
];

export default function OnboardingExperience() {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.1 });
  const [activeIndex, setActiveIndex] = useState(0);
  const [direction, setDirection] = useState(0);
  const timelineRef = useRef<HTMLDivElement>(null);

  const handleSelect = (index: number) => {
    setDirection(index > activeIndex ? 1 : -1);
    setActiveIndex(index);
  };

  useEffect(() => {
    if (timelineRef.current) {
      const buttons = timelineRef.current.querySelectorAll('button');
      const active = buttons[activeIndex];
      if (active) {
        active.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
      }
    }
  }, [activeIndex]);

  const phase = phases[activeIndex];

  const variants = {
    enter: (dir: number) => ({ opacity: 0, x: dir > 0 ? 40 : -40, y: 10 }),
    center: { opacity: 1, x: 0, y: 0 },
    exit: (dir: number) => ({ opacity: 0, x: dir > 0 ? -40 : 40, y: -10 }),
  };

  return (
    <section
      id="onboarding"
      className="py-16 md:py-24 bg-white relative overflow-hidden"
      ref={ref}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 40, scale: 0.95 }}
          animate={inView ? { opacity: 1, y: 0, scale: 1 } : {}}
          transition={{ ...springTransition, duration: 0.7 }}
          className="text-center mb-10 md:mb-16"
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={inView ? { opacity: 1, scale: 1 } : {}}
            transition={{ delay: 0.1, ...springTransition }}
            className="inline-flex items-center space-x-2 px-4 py-2 bg-primary-50 border border-primary-200 rounded-full mb-4 md:mb-6 shadow-sm"
          >
            <i className="ri-user-received-line text-primary-600 w-5 h-5 flex items-center justify-center"></i>
            <span className="text-sm font-medium text-gray-700">Your First 90 Days</span>
          </motion.div>

          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.15, ...springTransition }}
            className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-display font-bold text-navy-900 leading-tight mb-4 md:mb-6"
          >
            Onboarding
            <br />
            <span className="text-primary-600">Experience</span>
          </motion.h2>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.25, duration: 0.5 }}
            className="text-base md:text-xl text-gray-600 max-w-3xl mx-auto px-2"
          >
            From contract signature to full team integration — here is exactly what happens, who does what, and when you can expect results.
          </motion.p>
        </motion.div>

        {/* Timeline Pills */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ delay: 0.3, duration: 0.5 }}
          className="relative mb-8 md:mb-12"
        >
          {/* Progress track */}
          <div className="hidden md:block absolute top-1/2 left-0 right-0 h-0.5 -translate-y-1/2 bg-gray-200 rounded-full">
            <motion.div
              className="h-full bg-primary-500 rounded-full"
              initial={{ width: '0%' }}
              animate={{ width: `${(activeIndex / (phases.length - 1)) * 100}%` }}
              transition={{ type: 'spring', stiffness: 100, damping: 25 }}
            />
          </div>

          <div
            ref={timelineRef}
            className="flex md:justify-between gap-3 overflow-x-auto pb-4 md:pb-0 scrollbar-hide snap-x snap-mandatory"
          >
            {phases.map((p, i) => {
              const isActive = i === activeIndex;
              const isPast = i < activeIndex;
              return (
                <button
                  key={i}
                  onClick={() => handleSelect(i)}
                  className={`relative flex-shrink-0 snap-center flex flex-col items-center md:w-auto cursor-pointer group ${
                    isActive ? 'z-10' : ''
                  }`}
                >
                  {/* Dot */}
                  <div
                    className={`w-10 h-10 md:w-12 md:h-12 rounded-full flex items-center justify-center border-2 transition-all duration-300 mb-2 md:mb-3 ${
                      isActive
                        ? 'bg-primary-500 border-primary-500 shadow-lg shadow-primary-500/25 scale-110'
                        : isPast
                        ? 'bg-primary-500 border-primary-500'
                        : 'bg-white border-gray-300 group-hover:border-primary-400'
                    }`}
                  >
                    <i
                      className={`${p.icon} text-lg md:text-xl transition-colors ${
                        isActive || isPast ? 'text-white' : 'text-gray-500 group-hover:text-primary-500'
                      }`}
                    ></i>
                  </div>

                  {/* Label */}
                  <span
                    className={`text-xs md:text-sm font-semibold whitespace-nowrap transition-colors ${
                      isActive ? 'text-navy-900' : 'text-gray-500 group-hover:text-gray-700'
                    }`}
                  >
                    {p.label}
                  </span>
                  <span
                    className={`text-[10px] md:text-xs font-medium whitespace-nowrap transition-colors ${
                      isActive ? 'text-primary-600' : 'text-gray-400'
                    }`}
                  >
                    {p.timeline}
                  </span>
                </button>
              );
            })}
          </div>
        </motion.div>

        {/* Active Phase Content */}
        <AnimatePresence mode="wait" custom={direction}>
          <motion.div
            key={activeIndex}
            custom={direction}
            variants={variants}
            initial="enter"
            animate="center"
            exit="exit"
            transition={{ duration: 0.35, ease: 'easeInOut' }}
            className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-8"
          >
            {/* Left: Client Actions */}
            <div className="lg:col-span-4 bg-gray-50/80 border border-gray-100 rounded-2xl p-5 md:p-6">
              <div className="flex items-center gap-3 mb-4 md:mb-5">
                <div className="w-9 h-9 flex items-center justify-center bg-navy-900 rounded-lg">
                  <i className="ri-building-line text-white text-lg"></i>
                </div>
                <h3 className="text-base md:text-lg font-bold text-navy-900">What You Do</h3>
              </div>
              <ul className="space-y-3">
                {phase.clientActions.map((action, idx) => (
                  <motion.li
                    key={idx}
                    initial={{ opacity: 0, x: -15 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: idx * 0.08, duration: 0.3 }}
                    className="flex items-start gap-3"
                  >
                    <div className="w-5 h-5 flex-shrink-0 flex items-center justify-center mt-0.5">
                      <i className="ri-checkbox-circle-line text-primary-500 text-base"></i>
                    </div>
                    <span className="text-sm text-gray-700 leading-relaxed">{action}</span>
                  </motion.li>
                ))}
              </ul>
            </div>

            {/* Center: Thoughtic Actions */}
            <div className="lg:col-span-4 bg-primary-50/60 border border-primary-100 rounded-2xl p-5 md:p-6">
              <div className="flex items-center gap-3 mb-4 md:mb-5">
                <div className="w-9 h-9 flex items-center justify-center bg-primary-500 rounded-lg">
                  <i className="ri-shield-check-line text-white text-lg"></i>
                </div>
                <h3 className="text-base md:text-lg font-bold text-navy-900">What We Handle</h3>
              </div>
              <ul className="space-y-3">
                {phase.thoughticActions.map((action, idx) => (
                  <motion.li
                    key={idx}
                    initial={{ opacity: 0, x: -15 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: idx * 0.08 + 0.1, duration: 0.3 }}
                    className="flex items-start gap-3"
                  >
                    <div className="w-5 h-5 flex-shrink-0 flex items-center justify-center mt-0.5">
                      <i className="ri-check-double-line text-primary-600 text-base"></i>
                    </div>
                    <span className="text-sm text-gray-700 leading-relaxed">{action}</span>
                  </motion.li>
                ))}
              </ul>
            </div>

            {/* Right: Deliverables & Highlight */}
            <div className="lg:col-span-4 flex flex-col gap-4 md:gap-6">
              {/* Deliverables */}
              <div className="bg-white border border-gray-100 rounded-2xl p-5 md:p-6 flex-1">
                <div className="flex items-center gap-3 mb-4 md:mb-5">
                  <div className="w-9 h-9 flex items-center justify-center bg-emerald-50 border border-emerald-200 rounded-lg">
                    <i className="ri-file-list-3-line text-emerald-600 text-lg"></i>
                  </div>
                  <h3 className="text-base md:text-lg font-bold text-navy-900">Deliverables</h3>
                </div>
                <ul className="space-y-3">
                  {phase.deliverables.map((d, idx) => (
                    <motion.li
                      key={idx}
                      initial={{ opacity: 0, x: 15 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: idx * 0.08 + 0.15, duration: 0.3 }}
                      className="flex items-start gap-3"
                    >
                      <div className="w-5 h-5 flex-shrink-0 flex items-center justify-center mt-0.5">
                        <i className="ri-download-cloud-2-line text-emerald-500 text-sm"></i>
                      </div>
                      <span className="text-sm text-gray-700 leading-relaxed">{d}</span>
                    </motion.li>
                  ))}
                </ul>
              </div>

              {/* Highlight Card */}
              <motion.div
                initial={{ opacity: 0, y: 15 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3, duration: 0.4 }}
                className="bg-navy-900 rounded-2xl p-5 md:p-6 text-white"
              >
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 flex items-center justify-center bg-white/10 rounded-lg flex-shrink-0 mt-0.5">
                    <i className="ri-lightbulb-flash-line text-primary-400 text-lg"></i>
                  </div>
                  <div>
                    <p className="text-sm text-gray-300 leading-relaxed">{phase.highlight}</p>
                  </div>
                </div>
              </motion.div>
            </div>
          </motion.div>
        </AnimatePresence>

        {/* Comparison */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ delay: 0.5, ...springTransition }}
          className="mt-12 md:mt-16"
        >
          <div className="text-center mb-6 md:mb-8">
            <h3 className="text-xl md:text-2xl font-bold text-navy-900 mb-2">Typical Agency vs Thoughtic</h3>
            <p className="text-sm md:text-base text-gray-500">How our onboarding stacks up against industry norms</p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
            {comparison.map((item, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                animate={inView ? { opacity: 1, y: 0 } : {}}
                transition={{ delay: 0.55 + i * 0.08, ...springTransition }}
                className="bg-white border border-gray-100 rounded-xl p-4 md:p-5"
              >
                <div className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">Typical Agency</div>
                <p className="text-sm text-gray-600 mb-4 leading-relaxed">{item.typical}</p>

                <div className="h-px bg-gray-100 mb-4"></div>

                <div className="text-xs font-semibold text-primary-600 uppercase tracking-wider mb-3">Thoughtic</div>
                <p className="text-sm text-gray-800 font-medium mb-3 leading-relaxed">{item.thoughtic}</p>

                <div className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-primary-50 border border-primary-200 rounded-full">
                  <i className="ri-arrow-up-line text-primary-600 text-xs"></i>
                  <span className="text-xs font-bold text-primary-700">{item.metric}</span>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Bottom CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ delay: 0.7, ...springTransition }}
          className="mt-10 md:mt-14 flex flex-col sm:flex-row items-center justify-center gap-4"
        >
          <button
            onClick={() => {
              const el = document.getElementById('cta');
              if (el) {
                const offset = 80;
                const pos = el.getBoundingClientRect().top + window.pageYOffset - offset;
                window.scrollTo({ top: pos, behavior: 'smooth' });
              }
            }}
            className="px-8 py-3.5 bg-primary-500 text-white text-base font-semibold rounded-xl hover:shadow-xl hover:shadow-primary-500/20 hover:-translate-y-0.5 hover:brightness-110 transition-all duration-300 cursor-pointer whitespace-nowrap group"
          >
            Start Your Onboarding
            <i className="ri-arrow-right-line ml-2 group-hover:translate-x-1 transition-transform duration-300 inline-block"></i>
          </button>
          <button
            onClick={() => {
              const el = document.getElementById('process');
              if (el) {
                const offset = 80;
                const pos = el.getBoundingClientRect().top + window.pageYOffset - offset;
                window.scrollTo({ top: pos, behavior: 'smooth' });
              }
            }}
            className="px-8 py-3.5 border-2 border-gray-200 text-navy-900 text-base font-medium rounded-xl hover:border-primary-400 hover:text-primary-600 transition-all duration-300 cursor-pointer whitespace-nowrap"
          >
            See Full Process
          </button>
        </motion.div>
      </div>
    </section>
  );
}